/**
 * Frontend Widget Registry
 * 
 * Maps widget types to their configuration, rendering, and data fetching logic.
 */

import type { DashboardWidget } from './types';

export interface WidgetConfig {
  type: string;
  label: string;
  description: string;
  icon: string; // Emoji or icon identifier
  configFields: ConfigField[];
  filterFields: ConfigField[];
  defaults: {
    config: Record<string, any>;
    filters: Record<string, any>;
  };
}

export interface ConfigField {
  key: string;
  label: string;
  type: 'number' | 'string' | 'select';
  min?: number;
  max?: number;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  required?: boolean;
}

export const WIDGET_REGISTRY: Record<string, WidgetConfig> = {
  fleet_health: {
    type: 'fleet_health',
    label: 'Fleet Health',
    description: 'Shows online/offline/unknown counts for devices, gateways, and listeners',
    icon: '🏥',
    configFields: [
      {
        key: 'offlineAfterMinutes',
        label: 'Offline threshold (minutes)',
        type: 'number',
        min: 1,
        max: 1440,
        required: true
      }
    ],
    filterFields: [
      {
        key: 'deviceType',
        label: 'Device type filter',
        type: 'string',
        placeholder: 'e.g., soil_sensor'
      },
      {
        key: 'gatewayId',
        label: 'Gateway ID filter',
        type: 'string',
        placeholder: 'e.g., gateway_001'
      }
    ],
    defaults: {
      config: { offlineAfterMinutes: 15 },
      filters: {}
    }
  },

  message_rate: {
    type: 'message_rate',
    label: 'Message Rate',
    description: 'Time-series chart of message throughput',
    icon: '📊',
    configFields: [
      {
        key: 'windowMinutes',
        label: 'Time window (minutes)',
        type: 'number',
        min: 1,
        max: 10080,
        required: true
      },
      {
        key: 'bucketMinutes',
        label: 'Bucket size (minutes)',
        type: 'number',
        min: 1,
        max: 60,
        required: true
      }
    ],
    filterFields: [
      {
        key: 'deviceId',
        label: 'Device ID filter',
        type: 'string',
        placeholder: 'e.g., device_001'
      },
      {
        key: 'gatewayId',
        label: 'Gateway ID filter',
        type: 'string',
        placeholder: 'e.g., gateway_001'
      }
    ],
    defaults: {
      config: { windowMinutes: 60, bucketMinutes: 1 },
      filters: {}
    }
  },

  device_freshness_table: {
    type: 'device_freshness_table',
    label: 'Device Freshness Table',
    description: 'List of devices with last telemetry time and offline status',
    icon: '📋',
    configFields: [
      {
        key: 'offlineAfterMinutes',
        label: 'Offline threshold (minutes)',
        type: 'number',
        min: 1,
        max: 1440,
        required: true
      },
      {
        key: 'limit',
        label: 'Maximum devices to show',
        type: 'number',
        min: 1,
        max: 500,
        required: true
      }
    ],
    filterFields: [
      {
        key: 'deviceIdPrefix',
        label: 'Device ID prefix',
        type: 'string',
        placeholder: 'e.g., sensor_'
      },
      {
        key: 'deviceType',
        label: 'Device type',
        type: 'string',
        placeholder: 'e.g., soil_sensor'
      },
      {
        key: 'gatewayId',
        label: 'Gateway ID',
        type: 'string',
        placeholder: 'e.g., gateway_001'
      },
      {
        key: 'statusFilter',
        label: 'Status filter',
        type: 'select',
        options: [
          { value: 'all', label: 'All devices' },
          { value: 'online', label: 'Online only' },
          { value: 'offline', label: 'Offline only' },
          { value: 'unknown', label: 'Unknown only' }
        ]
      }
    ],
    defaults: {
      config: { offlineAfterMinutes: 15, limit: 50 },
      filters: { statusFilter: 'all' }
    }
  },

  top_noisiest_devices: {
    type: 'top_noisiest_devices',
    label: 'Top Noisiest Devices',
    description: 'Devices with the highest message counts in a time window',
    icon: '📢',
    configFields: [
      {
        key: 'windowMinutes',
        label: 'Time window (minutes)',
        type: 'number',
        min: 1,
        max: 1440,
        required: true
      },
      {
        key: 'limit',
        label: 'Number of devices',
        type: 'number',
        min: 1,
        max: 100,
        required: true
      }
    ],
    filterFields: [
      {
        key: 'deviceType',
        label: 'Device type filter',
        type: 'string',
        placeholder: 'e.g., soil_sensor'
      },
      {
        key: 'gatewayId',
        label: 'Gateway ID filter',
        type: 'string',
        placeholder: 'e.g., gateway_001'
      }
    ],
    defaults: {
      config: { windowMinutes: 60, limit: 10 },
      filters: {}
    }
  }
};

export function getWidgetConfig(type: string): WidgetConfig | null {
  return WIDGET_REGISTRY[type] || null;
}

export function listWidgetTypes(): WidgetConfig[] {
  return Object.values(WIDGET_REGISTRY);
}

export function createDefaultWidget(type: string, idSuffix: string): DashboardWidget | null {
  const config = getWidgetConfig(type);
  if (!config) return null;

  return {
    id: `${type}_${idSuffix}`,
    type: config.type,
    title: config.label,
    config: { ...config.defaults.config },
    filters: { ...config.defaults.filters }
  };
}
